import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { loadPagedFacilities } from '../actionCreators';
import { getFacilities } from '../selectors';
import { Card } from '@totalsoft/rocket-ui';
import GridOn from '@mui/icons-material/GridOn';
import HeaderPortal from 'components/layout/HeaderPortal';
import useDebounce from 'utils/hooks/useDebounce';
import ListFilter from '@ts-private/list-filter';
import FacilityList from './FacilityList';
import FacilityListFilters from './FacilityListFilters';
import { objToQueryString, clean, getListFilterLocalizedStrings, getDateValue } from 'utils/functions';
import WrappedFakeText from 'components/common/WrappedFakeText';
import PaginationWrapper from 'components/common/PaginationWrapper';
import { emptyString } from 'constants/emptyConst';

const blankFilters = {
  fullTextFilter: '',
  facilityNumber: '',
  partnerName: '',
  partnerGroupName: '',
  orderByColumnName: 'FacilityNumber',
  orderByDescending: false,
  validUntil: null,
  validFrom: null
};

const defaultPager = {
  page: 1,
  pageSize: 10
};

const FacilityListContainer = ({ history, location }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [filters, setFilters] = useState(blankFilters);
  const [pager, setPager] = useState(defaultPager);
  const debouncedEditFilters = useDebounce(filters, 400);

  const sortableColumns = [
    {
      columnName: 'FacilityNumber',
      displayName: t('Facility.FacilityNumber')
    }
  ];

  const {
    loading,
    content: facilities,
    totalCount
  } = useSelector(state => getFacilities(filters, pager.page, pager.pageSize, state), shallowEqual);

  const getFiltersFromURL = search => {
    const queryStringFilters = {
      partnerName: search.get('partnerName'),
      facilityNumber: search.get('facilityNumber'),
      partnerGroupName: search.get('partnerGroupName'),
      fullTextFilter: search.get('fullTextFilter'),
      orderByColumnName: search.get('orderByColumnName'),
      orderByDescending: JSON.parse(search.get('orderByDescending')),
      validUntil: search.get('validUntil'),
      validFrom: search.get('validFrom')
    };
    return queryStringFilters;
  };

  const pagerUpdater = useCallback(
    (prop, value) => {
      return prop === 'pageSize' ? { ...pager, [prop]: value, page: 1 } : { ...pager, [prop]: value };
    },
    [pager]
  );

  const pushFiltersAndPagingToQueryString = useCallback(
    (filters, pager) => {
      if (filters?.validFrom) filters.validFrom = getDateValue(filters.validFrom);
      if (filters?.validUntil) filters.validUntil = getDateValue(filters.validUntil);
      if (filters?.validFrom !== emptyString && filters?.validUntil !== emptyString) {
        const qs = getQueryStringFromFiltersAndPaging(filters, pager);
        if (qs !== '' && qs !== location.search) history.push('/facilities?' + qs);
      }
    },
    [history, location.search]
  );

  const onPageChange = useCallback(
    page => {
      const pager = pagerUpdater('page', page + 1);
      setPager(pager);
      pushFiltersAndPagingToQueryString(filters, pager);
    },
    [pagerUpdater, pushFiltersAndPagingToQueryString, filters]
  );

  const onRowsPerPageChange = useCallback(
    pageSize => {
      const pager = pagerUpdater('pageSize', pageSize);
      setPager(pager);
      pushFiltersAndPagingToQueryString(filters, pager);
    },
    [pagerUpdater, pushFiltersAndPagingToQueryString, filters]
  );

  const onChangeFilterValue = useCallback(
    (name, value) => {
      let newFilters = { ...filters, [name]: value };
      setFilters(newFilters);
      pushFiltersAndPagingToQueryString(newFilters, pager);
    },
    [filters, pager, pushFiltersAndPagingToQueryString]
  );

  const onResetFilters = () => {
    setFilters(blankFilters);
    setPager(defaultPager);
    pushFiltersAndPagingToQueryString(blankFilters, defaultPager);
  };

  const onRefresh = useCallback(() => {
    dispatch(loadPagedFacilities(filters, pager.page, pager.pageSize));
  }, [dispatch, filters, pager]);

  const updateFiltersFromUrl = useCallback(() => {
    const search = new URLSearchParams(location.search);
    const queryStringFilters = getFiltersFromURL(search);
    clean(queryStringFilters);

    if (Object.keys(queryStringFilters).length !== 0) {
      let changedFilters = { ...blankFilters, ...queryStringFilters };
      let changedPager = {
        page: parseInt(search.get('page'), 10) || pager.page,
        pageSize: parseInt(search.get('pageSize'), 10) || pager.pageSize
      };
      setFilters(changedFilters);
      setPager(changedPager);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  const getQueryStringFromFiltersAndPaging = (filters, pager) => {
    return objToQueryString({ ...filters, ...pager });
  };

  useEffect(() => {
    if (location.search != '') updateFiltersFromUrl();
  }, [location.search, updateFiltersFromUrl]);

  useEffect(() => {
    dispatch(loadPagedFacilities(debouncedEditFilters, pager.page, pager.pageSize));
  }, [debouncedEditFilters, dispatch, pager]);

  return (
    <>
      <Card icon={GridOn}>
        <ListFilter
          sortableColumns={sortableColumns}
          searchPlaceholder={t('Search')}
          filters={filters}
          onChangeFilterValue={onChangeFilterValue}
          downloadEnabled={false}
          entityName={'Facilities'}
          onResetFilters={onResetFilters}
          localizedStrings={getListFilterLocalizedStrings(t)}
        >
          <FacilityListFilters filters={filters} onChangeFilterValue={onChangeFilterValue} />
        </ListFilter>
        {loading ? <WrappedFakeText /> : <FacilityList facilities={facilities} />}
        <PaginationWrapper
          count={totalCount}
          pageSize={pager.pageSize}
          page={pager.page}
          onRowsPerPageChange={onRowsPerPageChange}
          onPageChange={onPageChange}
          onRefresh={onRefresh}
        />
      </Card>
      <HeaderPortal />
    </>
  );
};

FacilityListContainer.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired
};

export default FacilityListContainer;
